import type { PlayMode } from "../engine/scoring";
import type { HandFilter } from "../types";

/** App-level mode: the engine's wait/scored plus the Coach (drives scored). */
export type UIMode = PlayMode | "coach";

// Play preferences (mode / tempo / hand). Persisted to localStorage so the rig
// comes back the way you left it — alongside calibration (store/calibration.ts)
// and score history (store/scores.ts).

/** Camera follow mode for the sight-reading staff. */
export type StaffFollow = "auto" | "flow" | "page";

/** Rendering toggles (the Display section of the menu). */
export interface DisplayOptions {
  showKeyboard: boolean;
  /** Draw note letters (C D E F G A B) on the white-key slots. */
  slotLabels: boolean;
  /** Draw the note letter on each falling note. */
  noteLabels: boolean;
  /** Draw finger numbers on falling notes (reserved — needs Phase 3 fingering). */
  fingerLabels: boolean;
  /** Rotate the whole display 180° for an upside-down-mounted monitor. */
  rotate180: boolean;
  /** Show the schematic sight-reading staff above the highway. */
  staffEnabled: boolean;
  /** Index into CONFIG.staff.splitPresets (highway:staff ratio). */
  staffSplit: number;
  /** Falling-note highway opacity, 0..1 (1 = full crutch; 0 = read from the staff
   *  alone). The reading-tutor crutch fade — dimmable independently of the staff. */
  highwayOpacity: number;
  /** Page-mode systems to show; 0 = fit whole song (overview). */
  staffRows: number;
  /** Camera follow mode. */
  staffFollow: StaffFollow;
  /** Draw lead-sheet chord symbols above the staff (the terminal recall cue). */
  showChordSymbols: boolean;
  /** Fade the highway as you master a song (the challenge axis): per-song mastery
   *  dims the crutch toward 0 = play from memory. Combined min() with the reading
   *  fade. Off = today's behaviour. See songs/highway-fade.ts. */
  challengeFade: boolean;
}

interface Prefs {
  mode: UIMode;
  /** Tempo multiplier (1 = 100%). */
  tempo: number;
  /** Whole-song pitch shift in semitones. */
  transpose: number;
  hand: HandFilter;
  display: DisplayOptions;
  /** Audible metronome click on each beat. */
  metronome: boolean;
  /** Metronome click loudness, 0..1. */
  metronomeVolume: number;
  /** Beats of count-in clicked before playback starts (0 = none). */
  countInBeats: number;
  /** Whether the always-on piano "live strip" (bottom keys) is active. */
  pianoLiveKeys: boolean;
  /** Sound the unplayed hand through MIDI-out (F1 accompaniment), in scored/coach. */
  accompaniment: boolean;
  /** Last catalog song id, re-loaded on startup to restore where you left off
   *  ("" = none; a custom file import clears it — its bytes aren't kept). */
  lastSongId: string;
}

const KEY = "drop-notes.prefs";
const DEFAULT_DISPLAY: DisplayOptions = {
  showKeyboard: true,
  slotLabels: false,
  noteLabels: false,
  fingerLabels: false,
  rotate180: false,
  staffEnabled: false,
  staffSplit: 1,
  highwayOpacity: 1,
  staffRows: 1,
  staffFollow: "auto",
  showChordSymbols: false,
  challengeFade: false,
};
const DEFAULT: Prefs = {
  mode: "wait",
  tempo: 1,
  transpose: 0,
  hand: "both",
  display: DEFAULT_DISPLAY,
  metronome: true,
  metronomeVolume: 1,
  countInBeats: 0,
  pianoLiveKeys: true,
  accompaniment: false,
  lastSongId: "",
};

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function int(value: unknown, fallback: number, lo: number, hi: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(lo, Math.min(hi, Math.round(value)))
    : fallback;
}

function num01(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.min(1, value))
    : fallback;
}

function staffFollow(value: unknown): StaffFollow {
  return value === "flow" || value === "page" ? value : "auto";
}

export function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Prefs>;
      const d = (p.display ?? {}) as Partial<DisplayOptions>;
      return {
        // Coach is a per-session opt-in, never restored: a persisted "coach" (or
        // anything unrecognized) loads as a manual mode, so startup never lands in
        // a paused coach (which the C♯2 toggle would then read as "already on").
        mode: p.mode === "scored" ? "scored" : "wait",
        tempo: typeof p.tempo === "number" && p.tempo > 0 ? p.tempo : DEFAULT.tempo,
        transpose: Number.isFinite(p.transpose) ? (p.transpose as number) : 0,
        hand: p.hand === "left" || p.hand === "right" ? p.hand : "both",
        display: {
          showKeyboard: bool(d.showKeyboard, true),
          slotLabels: bool(d.slotLabels, false),
          noteLabels: bool(d.noteLabels, false),
          fingerLabels: bool(d.fingerLabels, false),
          rotate180: bool(d.rotate180, false),
          staffEnabled: bool(d.staffEnabled, false),
          staffSplit: int(d.staffSplit, 1, 0, 4),
          highwayOpacity: num01(d.highwayOpacity, 1),
          staffRows: int(d.staffRows, 1, 0, 12),
          staffFollow: staffFollow(d.staffFollow),
          showChordSymbols: bool(d.showChordSymbols, false),
          challengeFade: bool(d.challengeFade, false),
        },
        metronome: bool(p.metronome, true),
        metronomeVolume: num01(p.metronomeVolume, 1),
        countInBeats:
          typeof p.countInBeats === "number" && Number.isFinite(p.countInBeats)
            ? Math.max(0, Math.min(8, Math.round(p.countInBeats)))
            : 0,
        pianoLiveKeys: bool(p.pianoLiveKeys, true),
        accompaniment: bool(p.accompaniment, false),
        lastSongId: typeof p.lastSongId === "string" ? p.lastSongId : "",
      };
    }
  } catch {
    // ignore corrupt/blocked storage — fall through to defaults
  }
  return { ...DEFAULT, display: { ...DEFAULT_DISPLAY } };
}

export function savePrefs(prefs: Prefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // ignore — prefs simply won't persist
  }
}
